import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from '../ui/Button/Button';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { DraggableProductCard } from '../DragAndDrop/DraggableProductCard';
import { EvaluationLine } from '../DragAndDrop/EvaluationLine';
import { PreviewModal } from '../PreviewModal/PreviewModal';
import { ProductDetailModal } from '../ProductDetailModal/ProductDetailModal';
import { EditProductModal } from './ProductRegistration/EditProductModal';
import { useEvaluationStore } from '../../store/evaluationStore';

export const ComparisonEvaluation: React.FC = () => {
  const { t } = useTranslation();
  const { products, criteria, setCurrentStep, setEvaluationPosition, getEvaluationPosition, saveDataAsync } = useEvaluationStore();
  const [selectedCriterionIndex, setSelectedCriterionIndex] = React.useState(0);
  const [draggedProduct, setDraggedProduct] = React.useState<number | null>(null);
  const [draggedType, setDraggedType] = React.useState<'new' | 'placed' | null>(null);
  const [currentMousePosition, setCurrentMousePosition] = React.useState<{ x: number; y: number } | null>(null);
  const [previewModal, setPreviewModal] = React.useState<{ 
    isOpen: boolean; 
    url: string; 
    title: string;
    productIndex?: number;
  }>({
    isOpen: false,
    url: '',
    title: ''
  });

  const [detailModal, setDetailModal] = React.useState<{ isOpen: boolean; productIndex: number }>({
    isOpen: false,
    productIndex: 0
  });
  const [editingProductIndex, setEditingProductIndex] = React.useState<number | null>(null);

  // ドラッグ&ドロップのセンサー設定
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // より敏感に反応するよう変更
        tolerance: 5,
        delay: 0,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // マウス位置を追跡（ドラッグ中のみ）
  React.useEffect(() => {
    if (draggedProduct !== null) {
      const handleMouseMove = (event: MouseEvent) => {
        setCurrentMousePosition({ x: event.clientX, y: event.clientY });
      };

      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [draggedProduct]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeId = String(active.id);

    if (activeId.startsWith('placed-')) {
      // 配置済みアイテムの再配置
      const productIndex = parseInt(activeId.replace('placed-', ''));
      setDraggedProduct(productIndex);
      setDraggedType('placed');
    } else {
      // 新規配置
      setDraggedProduct(Number(active.id));
      setDraggedType('new');
    }
  };
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && over.id === 'evaluation-line' && lineRef.current) {
      const productIndex = Number(draggedProduct);
      const rect = lineRef.current.getBoundingClientRect();
      
      // イベントから直接座標を取得（より正確）
      const clientX = event.activatorEvent?.type === 'pointerup' 
        ? (event.activatorEvent as PointerEvent).clientX 
        : currentMousePosition?.x || 0;
        
      const relativeX = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));

      console.log('実際のドロップ座標デバッグ:', {
        productIndex,
        mouseX: clientX,
        rectLeft: rect.left,
        rectRight: rect.right,
        rectWidth: rect.width,
        calculation: (clientX - rect.left) / rect.width,
        relativeX,
        draggedType: draggedType,
        isInRange: clientX >= rect.left && clientX <= rect.right
      });

      // 重複回避の位置計算
      const position = calculatePositionWithOverlapAvoidance(Math.round(relativeX), productIndex);

      console.log('最終配置位置:', position);

      // 評価位置を設定
      setEvaluationPosition(productIndex, selectedCriterionIndex, position.x, position.y);
    } else {
      console.log('ドロップ失敗デバッグ:', {
        hasOver: !!over,
        overId: over?.id,
        hasMousePosition: !!currentMousePosition,
        hasLineRef: !!lineRef.current
      });
    }

    setDraggedProduct(null);
    setDraggedType(null);
  };  // 重複回避のための位置計算関数
  const calculatePositionWithOverlapAvoidance = (targetX: number, excludeProductIndex: number): { x: number; y: number } => {
    const placedPositions = products
      .map((_, index) => {
        if (index === excludeProductIndex) return null; // 現在ドラッグ中のアイテムは除外
        return getEvaluationPosition(index, selectedCriterionIndex);
      })
      .filter((pos): pos is { x: number; y: number } => pos !== null); let adjustedY = 0;
    const threshold = 15; // 重複判定の閾値を15%に設定

    // 同じようなx座標に配置されているアイテムを見つける
    const nearbyItems = placedPositions.filter(pos =>
      Math.abs(pos.x - targetX) < threshold
    );

    if (nearbyItems.length > 0) {
      // y座標を調整して重複を避ける（大幅な間隔で配置）
      const usedYPositions = nearbyItems.map(pos => pos.y).sort((a, b) => a - b);      // 既存のY位置から十分離れた位置を見つける
      let candidateY = 0;
      while (true) {
        const isTooClose = usedYPositions.some(usedY => Math.abs(candidateY - usedY) < 3);
        if (!isTooClose) {
          adjustedY = candidateY;
          break;
        }
        candidateY += 3; // Y座標の間隔を3単位に設定

        // 安全策として無限ループを防ぐ
        if (candidateY > 50) {
          adjustedY = candidateY;
          break;
        }
      }
    }

    console.log('重複回避計算:', {
      targetX,
      nearbyItemsCount: nearbyItems.length,
      adjustedY,
      threshold,
      placedPositions: placedPositions.map(p => `(${p.x}, ${p.y})`).join(', ')
    });

    return { x: targetX, y: adjustedY };
  };

  // EvaluationLineのrefを追加
  const lineRef = React.useRef<HTMLDivElement>(null);
  // 現在の観点での順位を計算する関数
  const calculateRankForCriterion = (productIndex: number, criterionIndex: number): number | undefined => {
    const placedProducts = products
      .map((_, index) => ({
        index,
        position: getEvaluationPosition(index, criterionIndex)
      }))
      .filter(item => item.position !== null)
      .sort((a, b) => (b.position?.x || 0) - (a.position?.x || 0)); // 高い順でソート

    const rank = placedProducts.findIndex(item => item.index === productIndex);
    return rank >= 0 ? rank + 1 : undefined;
  };

  // 比較評価を必要な分だけ実施（全体の10%または5個の多い方）
  const requiredComparisons = Math.max(5, Math.ceil(products.length * 0.1));
  
  // 各観点での配置済み数を計算
  const getPlacedCountForCriterion = (criterionIndex: number) => {
    return products.filter((_, productIndex) =>
      getEvaluationPosition(productIndex, criterionIndex) !== null
    ).length;
  };
  
  // 現在の観点で必要な最小配置数に達しているかチェック
  const isCurrentCriterionSufficient = getPlacedCountForCriterion(selectedCriterionIndex) >= requiredComparisons;
  
  // 全ての観点で最小配置数に達しているかチェック
  const canProceed = criteria.every((_, criterionIndex) =>
    getPlacedCountForCriterion(criterionIndex) >= requiredComparisons
  );

  // 後で使用できるようにクイック評価モードへのスキップ機能を追加
  const handleSkipToQuickMode = () => {
    setCurrentStep(6); // クイック評価モードへ
  };

  const handleNext = async () => {
    if (canProceed) {
      await saveDataAsync();
      setCurrentStep(4);
    }
  };

  const handlePrev = async () => {
    await saveDataAsync();
    setCurrentStep(2);
  };

  const openPreview = (url: string, title: string, productIndex?: number) => {
    setPreviewModal({
      isOpen: true,
      url,
      title,
      productIndex
    });
  };
  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: '',
      title: '',
      productIndex: undefined
    });
  };

  const openDetailModal = (productIndex: number) => {
    const product = products[productIndex];
    if (product && product.reviewLink) {
      // プレビューリンクがある場合は直接プレビューを開く
      openPreview(product.reviewLink, product.reviewTitle, productIndex);
    } else {
      // プレビューリンクがない場合は編集モーダルを開く
      setDetailModal({
        isOpen: true,
        productIndex: productIndex
      });
    }
  };

  const closeDetailModal = () => {
    setDetailModal({
      isOpen: false,
      productIndex: 0
    });
  };

  if (products.length === 0 || criteria.length === 0) {
    return (
      <section className="section">
        <div className="empty-state">
          <h2 className="empty-state__title">{t('comparisonEvaluation.emptyState.title')}</h2>
          <p className="empty-state__description">
            {t('comparisonEvaluation.emptyState.description')}
          </p>
          <div className="empty-state__actions">
            <Button
              variant="secondary"
              onClick={() => setCurrentStep(1)}
            >
              {t('comparisonEvaluation.emptyState.backToProducts')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCurrentStep(2)}
            >
              {t('comparisonEvaluation.emptyState.backToCriteria')}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  const currentCriterion = criteria[selectedCriterionIndex];

  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{t('comparisonEvaluation.title')}</h2>
      </div>

      <div className="comparison-evaluation">
        {/* 評価エリア */}
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="evaluation-area">
            {/* 数直線 */}
            <div className="evaluation-line-container">
              <div className="evaluation-line-labels">
                <span className="evaluation-line-label evaluation-line-label--start">{t('comparisonEvaluation.evaluationLine.low')}</span>
                <span className="evaluation-line-label evaluation-line-label--end">{t('comparisonEvaluation.evaluationLine.high')}</span>
              </div>
              <EvaluationLine
                ref={lineRef}
                criterionIndex={selectedCriterionIndex}
                products={products}
                getPosition={getEvaluationPosition}
                onProductDoubleClick={openDetailModal}
              />

              <div className="evaluation-line-scale">
                {[0, 25, 50, 75, 100].map(value => (
                  <div key={value} className="scale-mark" style={{ left: `${value}%` }}>
                    <div className="scale-mark__line" />
                    <div className="scale-mark__label">{value}</div>
                  </div>
                ))}
              </div>

              {/* 観点選択 - 配置領域内下部 */}
              <div className="criterion-selector criterion-selector--inline">
                <span className="criterion-selector__label">{t('comparisonEvaluation.criterionSelector.title')}</span>
                <div className="criterion-tabs">
                  {criteria.map((criterion, index) => {
                    const placedCount = getPlacedCountForCriterion(index);
                    const isSufficient = placedCount >= requiredComparisons;
                    const progressPercentage = requiredComparisons > 0 
                      ? Math.min((placedCount / requiredComparisons) * 100, 100)
                      : 0;
                    
                    return (
                      <button
                        key={index}
                        className={`criterion-tab ${index === selectedCriterionIndex ? 'active' : ''} ${isSufficient ? 'sufficient' : ''}`}
                        onClick={() => setSelectedCriterionIndex(index)}
                      >
                        <span className="criterion-tab__title">{criterion.title}</span>
                        <div className="criterion-tab__progress-bar">
                          <div 
                            className="criterion-tab__progress-fill" 
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* 評価対象リスト - 横並び表示 */}
            <div className="products-panel products-panel--horizontal">
              <h4 className="products-panel__title">{t('comparisonEvaluation.productsPanel.title')}</h4>
              <SortableContext items={products.map((_, i) => i)} strategy={verticalListSortingStrategy}>
                <div className="product-items product-items--horizontal">
                  {products.map((product, index) => {
                    const position = getEvaluationPosition(index, selectedCriterionIndex);
                    const rank = position ? calculateRankForCriterion(index, selectedCriterionIndex) : undefined;

                    return (
                      <DraggableProductCard
                        key={index}
                        id={index}
                        product={product}
                        isPlaced={position !== null}
                        onPreview={openPreview}
                        onDoubleClick={openDetailModal}
                        position={position || undefined}
                        rank={rank}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </div>
          </div>          <DragOverlay dropAnimation={null}>
            {draggedProduct !== null && currentMousePosition && (
              <div
                style={{
                  background: 'var(--color-background)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  cursor: 'grabbing',
                }}
              >
                {products[draggedProduct].reviewTitle}
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* 進捗表示 - 一時的に非表示 */}
        {/* <div className="evaluation-progress">
          <div className="evaluation-progress__summary">
            <span className="evaluation-progress__text">
              {t('comparisonEvaluation.progress.prefix')}{t('comparisonEvaluation.progress.suffix', {
                placed: products.filter((_, productIndex) =>
                  getEvaluationPosition(productIndex, selectedCriterionIndex) !== null
                ).length,
                total: products.length
              })}
            </span>
            {canProceed && (
              <span className="evaluation-progress__complete">
                ✅ {t('comparisonEvaluation.progress.completed')}
              </span>
            )}
          </div>

          <div className="evaluation-progress__bar">
            <div
              className="evaluation-progress__fill"
              style={{
                width: `${(products.filter((_, productIndex) =>
                  getEvaluationPosition(productIndex, selectedCriterionIndex) !== null
                ).length / products.length) * 100}%`
              }}
            />
          </div>        </div> */}
      </div>

      {/* 比較評価のオプション - 一時的に非表示 */}
      {/* <div className="evaluation-options">
        <div className="evaluation-options__info">
          <h4>{t('comparisonEvaluation.options.title')}</h4>
          <p>{t('comparisonEvaluation.options.description')}</p>
        </div>
        <div className="evaluation-options__buttons">
          <Button
            variant="secondary"
            onClick={handleSkipToQuickMode}
          >
            {t('comparisonEvaluation.options.quickModeButton')}
          </Button>
          <span className="evaluation-options__note">
            {t('comparisonEvaluation.options.quickModeNote')}
          </span>
        </div>
      </div> */}

      <StepNavigation
        currentStep={3}
        canProceed={canProceed}
        onNext={handleNext}
        onPrev={handlePrev}
        nextLabel={t('comparisonEvaluation.nextButton')}
        prevLabel={t('comparisonEvaluation.prevButton')}
      />      {/* プレビュー用モーダル */}
      <PreviewModal
        isOpen={previewModal.isOpen}
        url={previewModal.url}
        title={previewModal.title}
        productIndex={previewModal.productIndex}
        onClose={closePreview}
      />

      {/* 詳細表示モーダル */}
      <ProductDetailModal
        isOpen={detailModal.isOpen}
        productIndex={detailModal.productIndex}
        onClose={closeDetailModal}
        onPreview={openPreview}
      />

      {/* 製品編集モーダル */}
      {editingProductIndex !== null && (
        <EditProductModal
          isOpen={true}
          productIndex={editingProductIndex}
          onClose={() => setEditingProductIndex(null)}
          onNavigate={(direction) => {
            const newIndex = direction === 'prev' 
              ? Math.max(0, editingProductIndex - 1) 
              : Math.min(products.length - 1, editingProductIndex + 1);
            setEditingProductIndex(newIndex);
          }}
        />
      )}
    </section>
  );
};